// app/frontend/src/components/stakeholders-list/index.tsx
import type { stakeholdersList_StakeholdersListQuery } from "@app/queries/stakeholdersList_StakeholdersListQuery.graphql";
import { environment } from "@app/relay/relay-environment";
import { useEffect, useRef, useState } from "react";
import { fetchQuery, graphql, usePreloadedQuery } from "react-relay";
import type { PreloadedQuery } from "react-relay";
import StakeholderRow from "../stakeholder-row";

// Heavier rows (a monthly-cost sparkline per row, plus a lazily-loaded txns/
// streams detail on expand) → a smaller page.
export const PAGE_SIZE = 50;

// One page of stakeholders (name A–Z) plus the total for the pager. Each row
// spreads the row fragment, which now loads its txns/streams only on expand.
export const stakeholdersQuery = graphql`
  query stakeholdersList_StakeholdersListQuery(
    $search: String
    $limit: Int
    $offset: Int
    $initialStart: String!
    $initialEnd: String!
  ) {
    stakeholders(search: $search, limit: $limit, offset: $offset) {
      totalCount
      stakeholders {
        id
        name
        ...stakeholderRow_stakeholder
          @arguments(startDate: $initialStart, endDate: $initialEnd)
      }
    }
  }
`;

type Payload = stakeholdersList_StakeholdersListQuery["response"]["stakeholders"];

export default function StakeholdersList({
  queryRef,
}: {
  queryRef: PreloadedQuery<stakeholdersList_StakeholdersListQuery>;
}) {
  const data = usePreloadedQuery(stakeholdersQuery, queryRef);
  const { initialStart, initialEnd } = queryRef.variables;

  const [list, setList] = useState<Payload>(data.stakeholders);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  // Fresh route load: reseed from the preloaded page.
  useEffect(() => {
    setList(data.stakeholders);
  }, [data]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Refetch on every search / page change (skip the first render — the loader
  // already fetched page 0).
  const firstRender = useRef(true);
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setIsFetching(true);
    fetchQuery<stakeholdersList_StakeholdersListQuery>(
      environment,
      stakeholdersQuery,
      {
        search: debouncedSearch.trim() || null,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        initialStart,
        initialEnd,
      },
      { fetchPolicy: "network-only" }
    )
      .toPromise()
      .then((res) => {
        if (res?.stakeholders) setList(res.stakeholders);
      })
      .finally(() => setIsFetching(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, page]);

  const rows = list.stakeholders;
  const totalCount = list.totalCount;
  const rangeStart = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const rangeEnd = Math.min((page + 1) * PAGE_SIZE, totalCount);
  const hasPrev = page > 0;
  const hasNext = (page + 1) * PAGE_SIZE < totalCount;

  const pager =
    totalCount > PAGE_SIZE ? (
      <div className="flex items-center justify-between text-sm text-gray-500 py-2">
        <span>
          Showing {rangeStart.toLocaleString()}–{rangeEnd.toLocaleString()} of{" "}
          {totalCount.toLocaleString()}
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            disabled={!hasPrev}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-30"
          >
            ← Prev
          </button>
          <span className="text-xs">
            Page {page + 1} of {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
          </span>
          <button
            type="button"
            disabled={!hasNext}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 rounded border hover:bg-gray-50 disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Search stakeholders by name…"
          className="min-w-64 flex-1 rounded border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-emerald-400"
        />
        <span className="text-sm text-gray-500">
          {totalCount.toLocaleString()} stakeholder{totalCount === 1 ? "" : "s"}
        </span>
        {isFetching && (
          <span
            className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-gray-300 border-t-gray-600"
            role="status"
            aria-label="Loading stakeholders"
          />
        )}
      </div>

      {pager}

      <div
        className={`rounded-md border transition-opacity duration-200 ${
          isFetching ? "opacity-50" : ""
        }`}
        aria-busy={isFetching}
      >
        <table className="w-full text-sm text-left caption-bottom">
          <thead className="[&_tr]:border-b">
            <tr className="border-b transition-colors hover:bg-muted/50 data-[state=selected]:bg-muted">
              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                Name
              </th>
              <th
                colSpan={12}
                className="h-12 px-4 text-left align-middle font-medium text-muted-foreground"
              >
                Monthly Costs
              </th>
              <th className="h-12 px-4 text-left align-middle font-medium text-muted-foreground">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="[&_tr:last-child]:border-0">
            {rows.map((stakeholder, index) => (
              <StakeholderRow
                key={stakeholder.id}
                name={stakeholder.name}
                stakeholderKey={stakeholder}
                isEven={index % 2 !== 0}
              />
            ))}
          </tbody>
        </table>
        {rows.length === 0 && (
          <p className="p-6 text-sm text-gray-400">
            No stakeholders match this search.
          </p>
        )}
      </div>

      {pager}
    </div>
  );
}
